import type { Seat } from '@campfire/engine';

/**
 * Claim-vs-truth (ADR-0004, FR-BLUFF-01). A seat lays down hidden items and states what
 * they are; other seats may call the bluff before play moves on. Games decide the stakes.
 */
export interface Claim<T> {
  readonly claimant: Seat;
  /** What the claimant says the items are, e.g. a rank. */
  readonly claimed: string;
  /** The hidden truth. Never put this in a view until it is revealed. */
  readonly items: readonly T[];
}

export interface ChallengeWindow<T> {
  readonly claim: Claim<T>;
  /** Seats that have not yet called or let it go. The window closes when this is empty. */
  readonly waiting: readonly Seat[];
}

export type Response = 'call' | 'pass';

export type WindowStep<T> =
  | { readonly kind: 'open'; readonly window: ChallengeWindow<T> }
  | { readonly kind: 'accepted'; readonly claim: Claim<T> }
  | { readonly kind: 'called'; readonly claim: Claim<T>; readonly caller: Seat; readonly truthful: boolean };

/** Every seat except the claimant may challenge, in seat order after the claimant. */
export function openWindow<T>(claim: Claim<T>, seats: number): ChallengeWindow<T> {
  const waiting = Array.from({ length: seats - 1 }, (_, k) => (claim.claimant + 1 + k) % seats);
  return { claim, waiting };
}

/**
 * Apply one seat's response. The first call settles the claim at once; passes shrink the
 * window until everyone has let it go. `isTrue` decides whether one hidden item matches the claim.
 */
export function respond<T>(w: ChallengeWindow<T>, seat: Seat, response: Response, isTrue: (item: T, claimed: string) => boolean): WindowStep<T> {
  if (!w.waiting.includes(seat)) throw new Error(`Seat ${seat} cannot respond to this claim`);
  if (response === 'call') {
    const truthful = w.claim.items.every((i) => isTrue(i, w.claim.claimed));
    return { kind: 'called', claim: w.claim, caller: seat, truthful };
  }
  const waiting = w.waiting.filter((s) => s !== seat);
  return waiting.length === 0 ? { kind: 'accepted', claim: w.claim } : { kind: 'open', window: { ...w, waiting } };
}
